/**
 * @license observable-hooks
 * index.ts
 *
 * Copyright (c) 2023 CRIMX<straybugs@gmail.com>
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
/* eslint-disable react-hooks/rules-of-hooks */
import { useEffect, useRef, useState } from 'react';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import type { Observable } from 'rxjs/Observable';

/**
 * @template TOutput Output value within Observable.
 * @template TInputs A readonly tuple of all dependencies.
 *
 * @param useCustomEffect useEffect or useLayoutEffect
 * @param init A pure function that, when applied to an Observable,
 * returns an Observable.
 * @param inputs An dependency array with fixed length. When one of the dependencies
 * changes the Observable in `init` will emit an array of all the dependencies.
 */
function useObservableInternal<TOutput, TInputs extends Readonly<any[]>>(
	init: (() => Observable<TOutput>) | ((inputs$: Observable<[...TInputs]>) => Observable<TOutput>),
	inputs?: [...TInputs],
): Observable<TOutput> {
	// Even though hooks are under conditional block
	// it is for a completely different use case
	// which unlikely coexists with the other one.
	// A warning is also added to the docs.
	if (!inputs) {
		return useState(init as () => Observable<TOutput>)[0];
	}

	const [inputs$] = useState(() => new BehaviorSubject(inputs));
	const [source$] = useState(() => init(inputs$));

	const firstEffectRef = useRef(true);
	useEffect(() => {
		if (firstEffectRef.current) {
			firstEffectRef.current = false;
			return;
		}
		inputs$.next(inputs);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, inputs);

	return source$;
}

/**
 * Accepts a function that returns an Observable.
 * Optionally accepts an array of dependencies which
 * will be turned into Observable and be passed to the
 * `init` function.
 *
 * React functional components are called many times during their lifecycle.
 * Create or transform Observables in `init` function so that the operations
 * won't be repeatedly performed.
 *
 * ⚠ **Note:** `useObservable` will call `init` once and always return
 * the same Observable. It is not safe to access closure (except other Observables)
 * directly inside `init`.
 * You should use ref or pass them as dependencies through the second argument.
 *
 * ⚠ **Note:** Due to rules of hooks you can either offer or omit the
 * dependencies array but do not change to one another during Component's life cycle.
 * The length of the dependencies array must also be fixed.
 *
 * @template TOutput Output value in Observable
 *
 * @param init A pure function that, when applied to an Observable,
 * returns an Observable.
 */
export function useObservable<
	TOutput,
	TObservable extends Observable<TOutput> = Observable<TOutput>,
>(init: () => TObservable): TObservable;
/**
 * @template TOutput Output value within Observable.
 * @template TInputs A readonly tuple of all dependencies.
 *
 * @param init A pure function that, when applied to an Observable,
 * returns an Observable.
 * @param inputs An dependency array with fixed length. When one of the dependencies
 * changes the Observable in `init` will emit an array of all the dependencies.
 */
export function useObservable<
	TOutput,
	TInputs extends Readonly<any[]>,
	TObservable extends Observable<TOutput> = Observable<TOutput>,
>(init: (inputs$: Observable<[...TInputs]>) => TObservable, inputs: [...TInputs]): TObservable;
export function useObservable<TOutput, TInputs extends Readonly<any[]>>(
	init: (() => Observable<TOutput>) | ((inputs$: Observable<[...TInputs]>) => Observable<TOutput>),
	inputs?: [...TInputs],
): Observable<TOutput> {
	return useObservableInternal(init, inputs);
}
